import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Table,
  Row,
  Col,
  Badge,
  Alert,
  Card,
  CardBody
} from "react-bootstrap";
import axios from "axios";
import { API_ENDPOINTS } from '../config/api';
import ConferenceDetailsForm from "./ConferenceDetailsForm";

const ConferenceDetailsModal = ({ show, onHide, date, onSessionClick }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDetailsForm, setShowDetailsForm] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  useEffect(() => {
    if (show && date) {
      fetchSessionsForDate();
    }
  }, [show, date]);

  const fetchSessionsForDate = async () => {
    if (!date) return;
    
    try {
      setLoading(true);
      const dateStr = date.toISOString().split('T')[0];
      const response = await axios.get(`${API_ENDPOINTS.CONFERENCE_SESSIONS}/date/${dateStr}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      console.log('Fetched sessions:', response.data); // Debug log
      setSessions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching sessions:", error);
      console.error("Error response:", error.response?.data); // Debug log
      setError("Failed to fetch conference sessions");
      setSessions([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleSessionClick = (session) => {
    onSessionClick(session);
  };

  const handleAddDetails = (session) => {
    setSelectedSession(session);
    setShowDetailsForm(true);
  };

  const handleDetailsSaved = () => {
    setShowDetailsForm(false);
    fetchSessionsForDate(); // Refresh the data
  };

  const getConferenceTypeLabel = (type) => {
    const labels = {
      'mm': 'M&M',
      'ground_rounds': 'Ground Rounds',
      'both': 'Both'
    };
    return labels[type] || type;
  };

  const getConferenceTypeColor = (type) => {
    const colors = {
      'mm': 'primary',
      'ground_rounds': 'success',
      'both': 'warning'
    };
    return colors[type] || 'secondary';
  };

  const renderSessionDetails = (session) => {
    const conference = session.conference;
    const attendance = session.attendance || [];
    const attendedCount = attendance.filter(a => a.status === 'attended').length;
    const totalCount = attendance.length;

    return (
      <Card key={session.id} className="mb-3">
        <CardBody>
          <Row>
            <Col md={8}>
              <h6 className="mb-2">
                {conference.title}
                <Badge 
                  bg={getConferenceTypeColor(conference.type)} 
                  className="ms-2"
                >
                  {getConferenceTypeLabel(conference.type)}
                </Badge>
              </h6>
              <p className="text-muted mb-2">
                {conference.startTime} - {conference.endTime}
              </p>
              {session.speaker && (
                <p className="mb-1">
                  <strong>Speaker:</strong> {session.speaker}
                  {session.speakerTitle && ` (${session.speakerTitle})`}
                </p>
              )}
              {session.talkTitle && (
                <p className="mb-1">
                  <strong>Talk:</strong> {session.talkTitle}
                </p>
              )}
              {session.talkTime && (
                <p className="mb-1">
                  <strong>Talk Time:</strong> {session.talkTime}
                </p>
              )}
              <p className="mb-2">
                <strong>Attendance Code:</strong> <code>{session.attendanceCode}</code>
              </p>
              <p className="mb-0">
                <strong>Attendance:</strong> {attendedCount}/{totalCount} attended
              </p>
            </Col>
            <Col md={4} className="text-end">
              <div className="d-grid gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleSessionClick(session)}
                >
                  Track Attendance
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleAddDetails(session)}
                >
                  Add Details
                </Button>
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={() => handleExportToCalendar(session)}
                >
                  Export to Calendar
                </Button>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>
    );
  };

  const handleExportToCalendar = async (session) => {
    try {
      const conference = session.conference;
      const attendeeIds = conference.requiredAttendees || [];
      
      const response = await axios.post(
        `${API_ENDPOINTS.CONFERENCE_SESSIONS}/${session.id}/export-calendar`,
        { attendeeIds },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'x-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        }
      );
      
      alert(`Successfully exported ${response.data.events} events to calendar`);
    } catch (error) {
      console.error("Error exporting to calendar:", error);
      alert("Failed to export to calendar");
    }
  };

  const renderAttendanceList = (session) => {
    const attendance = session.attendance || [];
    
    if (attendance.length === 0) {
      return <p className="text-muted">No attendance records yet</p>;
    }

    return (
      <Table size="sm">
        <thead>
          <tr>
            <th>Name</th>
            <th>Status</th>
            <th>Recorded At</th>
          </tr>
        </thead>
        <tbody>
          {attendance.map((record) => (
            <tr key={record.id}>
              <td>{record.user.firstName} {record.user.lastName}</td>
              <td>
                <Badge 
                  bg={record.status === 'attended' ? 'success' : 
                      record.status === 'not_attended' ? 'danger' : 'warning'}
                >
                  {record.status}
                </Badge>
              </td>
              <td>
                {record.recordedAt ? 
                  new Date(record.recordedAt).toLocaleString() : 
                  'Not recorded'
                }
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  if (!date) return null;

  const dateStr = date.toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Conference Details - {dateStr}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          {loading ? (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : !Array.isArray(sessions) || sessions.length === 0 ? (
            <Alert variant="info">
              No conferences scheduled for this date.
            </Alert>
          ) : (
            <div>
              {Array.isArray(sessions) && sessions.map(renderSessionDetails)}
              
              {Array.isArray(sessions) && sessions.length > 0 && (
                <div className="mt-4">
                  <h6>Attendance Summary</h6>
                  {sessions.map(session => (
                    <div key={session.id} className="mb-3">
                      <h6 className="mb-2">{session.conference.title}</h6>
                      {renderAttendanceList(session)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Conference Details Form Modal */}
      <ConferenceDetailsForm
        show={showDetailsForm}
        onHide={() => setShowDetailsForm(false)}
        session={selectedSession}
        onDetailsSaved={handleDetailsSaved}
      />
    </>
  );
};

export default ConferenceDetailsModal; 